import { Common } from "@common/types";
import { identity, last } from "lodash";
import React from "react";

export type ClickOutsideContextListener = {
  callback: () => void;
  cannotClose?: boolean;
  ignoreRefs: Array<React.RefObject<any> | undefined | null>;
};

type ClickOutsideContextListenerInner = ClickOutsideContextListener & {
  id: string;
};

// TODO: better handling. Suggestion: wrap every usage with an fixed overlay 100vw/100vh transparent. Trigger unsubscribe on click by it
const IGNORE_SELECTORS = [
  ".pac-container", // Google maps dropdown container
  "GRAMMARLY-POPUPS", // grammarly elements
  ".tour_modal", // guide modal
  ".tour_overlay", // guide modal overlay
  ".modal.top_most"
];
export interface ClickOutsideContextProps {
  listeners: ClickOutsideContextListenerInner[];
  subscribe: (id: string, listener: ClickOutsideContextListener) => void;
  resubscribe: (id: string, listener: ClickOutsideContextListener) => void;
  unsubscribe: (id: string) => void;
}

export const ClickOutsideContext = React.createContext<ClickOutsideContextProps>({
  listeners: [],
  subscribe: identity,
  resubscribe: identity,
  unsubscribe: identity
});

export const ClickOutsideContextProvider = (props: Common.WithChildren) => {
  const contextValue = React.useMemo<ClickOutsideContextProps>(() => {
    let listeners: ClickOutsideContextListenerInner[] = [];
    return {
      listeners,
      subscribe: (id: string, listener: ClickOutsideContextListener) => {
        listeners.push({ id, ...listener });
      },
      resubscribe: (id: string, listener: ClickOutsideContextListener) => {
        const index = listeners.findIndex((x) => x.id === id);
        if (index === -1) {
          throw new Error("BUG");
        }
        listeners[index] = { id, ...listener };
      },
      unsubscribe: (id: string) => {
        const index = listeners.findIndex((x) => x.id === id);
        if (index === -1) {
          throw new Error("BUG");
        }

        // NOTE: unsubscribe on parent can be called before child (e.g. ESC press with an open modal)
        listeners.splice(index, 1);
      }
    };
  }, []);

  React.useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || !contextValue.listeners.length) return;

      const listener = last(contextValue.listeners)!;
      if (
        listener &&
        !listener.cannotClose &&
        e.target &&
        IGNORE_SELECTORS.every((x) => !$(e.target!).closest(x).length) &&
        listener.ignoreRefs.every((x) => {
          if (!x?.current) return true;

          const node = x.current as HTMLElement;
          return node && !$(e.target!).closest(node).length && !$(e.target!).closest(".no-close-click-outside").length;
        })
      ) {
        listener.callback();
      }
    };
    window.addEventListener("mousedown", onMouseDown, true);

    return () => {
      window.removeEventListener("mousedown", onMouseDown, true);
    };
  }, [contextValue]);

  return <ClickOutsideContext.Provider value={contextValue}>{props.children}</ClickOutsideContext.Provider>;
};
